import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import { LegalLinks } from '../legal/LegalLinks';
import { 
  Menu, 
  Target, 
  TrendingUp, 
  Brain, 
  Shield, 
  BookOpen,
  ChevronRight,
  Home,
  Dumbbell,
  Weight
} from 'lucide-react';

const modules = [
  { 
    id: 'module1', 
    path: '/module1', 
    name: 'Module 1', 
    title: 'Baseline & Setup',
    icon: Target,
    description: 'Equipment, technique, baseline test'
  },
  { 
    id: 'module2', 
    path: '/module2', 
    name: 'Module 2', 
    title: 'Programming',
    icon: TrendingUp,
    description: 'Load management & progression'
  },
  { 
    id: 'module3', 
    path: '/module3', 
    name: 'Module 3', 
    title: 'Mindset',
    icon: Brain,
    description: 'Expectation management'
  },
  { 
    id: 'module4', 
    path: '/module4', 
    name: 'Module 4', 
    title: 'Pain & Constraints',
    icon: Shield,
    description: 'Coming soon',
    comingSoon: true
  },
  { 
    id: 'module5', 
    path: '/module5', 
    name: 'Module 5', 
    title: 'Accessory Work',
    icon: Dumbbell,
    description: 'Coming soon',
    comingSoon: true
  },
  { 
    id: 'module6', 
    path: '/module6', 
    name: 'Module 6', 
    title: 'Load & Equipment',
    icon: Weight,
    description: 'Rep cycling & load mastery'
  },
  { 
    id: 'reference', 
    path: '/reference', 
    name: 'Reference', 
    title: 'Technique Deep Dive',
    icon: BookOpen,
    description: 'Always accessible',
    isReference: true
  }
];

const NavItem = ({ module, onClick }) => {
  const Icon = module.icon;

  if (module.comingSoon) {
    return (
      <div
        className="block w-full px-4 py-3 border-l-2 border-transparent opacity-50 cursor-default select-none"
        aria-disabled="true"
        data-testid={`nav-${module.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-none flex items-center justify-center flex-shrink-0 bg-mono-surface text-mono-mute">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-heading text-sm font-bold uppercase tracking-wider text-white">
                {module.name}
              </span>
              <span className="text-[10px] border border-mono-line text-mono-mute px-1.5 py-0.5 rounded-none uppercase font-bold">
                Soon
              </span>
            </div>
            <p className="text-xs truncate text-mono-mute">
              {module.description}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={module.path}
      onClick={onClick}
      className={({ isActive }) => `
        block w-full px-4 py-3 transition-colors duration-200 
        ${module.isReference ? 'border-t border-mono-line mt-4 pt-6' : ''}
        ${isActive 
          ? 'bg-crimson/10 border-l-2 border-crimson' 
          : 'border-l-2 border-transparent hover:bg-mono-surface/60 hover:border-mono-line'
        }
      `}
      data-testid={`nav-${module.id}`}
    >
      {({ isActive }) => (
        <div className="flex items-center gap-3">
          <div className={`
            w-8 h-8 rounded-none flex items-center justify-center flex-shrink-0
            ${isActive ? 'bg-crimson text-white' : 'bg-mono-surface text-mono-mute'}
            ${module.isReference && !isActive ? 'bg-mono-line' : ''}
          `}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`
                font-heading text-sm font-bold uppercase tracking-wider
                ${isActive ? 'text-crimson' : 'text-white'}
              `}>
                {module.name}
              </span>
              {module.comingSoon && (
                <span className="text-[10px] border border-mono-line text-mono-mute px-1.5 py-0.5 rounded-none uppercase font-bold">
                  Soon
                </span>
              )}
            </div>
            <p className={`
              text-xs truncate
              ${isActive ? 'text-white' : 'text-mono-mute'}
            `}>
              {module.description}
            </p>
          </div>
          <ChevronRight className={`
            h-4 w-4 flex-shrink-0 transition-transform
            ${isActive ? 'text-crimson translate-x-1' : 'text-mono-mute'}
          `} />
        </div>
      )}
    </NavLink>
  );
};

const SidebarContent = ({ onItemClick }) => {
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col h-full">
      {/* Logo / Brand */}
      <div 
        className="p-6 border-b border-mono-line cursor-pointer hover:bg-mono-surface/40 transition-colors"
        onClick={() => {
          navigate('/program');
          onItemClick?.();
        }}
        data-testid="sidebar-logo"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-crimson rounded-none flex items-center justify-center">
            <span className="font-heading font-black text-white text-lg">CR</span>
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white uppercase tracking-tight leading-none">
              Coach Rands
            </h1>
            <p className="text-crimson text-xs font-bold uppercase tracking-widest">
              0 → 20KG
            </p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {modules.map(module => (
          <NavItem 
            key={module.id} 
            module={module} 
            onClick={onItemClick}
          />
        ))}
      </nav>
      
      {/* Footer */}
      <div className="p-4 border-t border-mono-line space-y-3">
        <p className="text-mono-mute text-xs text-center font-body">
          12-Week Weighted Pull-Up Program
        </p>
        <LegalLinks className="justify-center gap-x-4" />
      </div>
    </div>
  );
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-mono-base/95 backdrop-blur-sm border-b border-mono-line px-4 py-3">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:text-white hover:bg-mono-surface"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent 
            side="left" 
            className="w-72 p-0 bg-mono-base border-mono-line"
          >
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SidebarContent onItemClick={() => setIsOpen(false)} />
          </SheetContent>
        </Sheet>
        <span className="ml-4 font-heading font-bold text-white uppercase tracking-tight">
          Coach Rands
        </span>
      </div>

      {/* Desktop Sidebar */}
      <aside 
        className="hidden md:flex md:flex-col md:w-64 md:fixed md:inset-y-0 bg-mono-base border-r border-mono-line"
        data-testid="desktop-sidebar"
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
